import React, { useContext } from 'react'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import { Icon } from '~/components/atoms/Icon/Icon'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { modalTypes } from '~/components/modalLoader'
import { ReportTheme } from '~/components/report/types'
import { useAuthV2 } from '~/contexts/AuthContext'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { AUTH_CONFIG } from '~/pages/api/auth/auth-config'
import { getLocalStorage } from '~/utils/webStorage'

import ListItemWithTick from '../ListItemWithTick/ListItemWithTick'

import styles from './TierList.module.css'

interface TierBlockProps {
  _ref: string
  productTitle?: string | React.ReactNode
  productText?: string | React.ReactNode
  showInfoModalIcon?: boolean
  buttonText?: string
  infoModalReference?: string
  isMostPopular?: boolean
  theme: {
    backgroundVariant: 'white' | 'grey' | 'blue'
  }
  introText?: string
  featureList?: string[]
  infoModalText?: string
  onModalCallback?: (ref: string, props?: any) => void
}

const TierBlock = (props: TierBlockProps) => {
  const { user, showVerifyDialog } = useAuthV2()

  const onPrimaryButtonClick = () => {
    if (!props.onModalCallback) return

    if (props.infoModalReference === 'speakToOurTeamDialog') {
      props.onModalCallback('speakToOurTeamDialog', {
        whiteCloseBtn: true,
      })
      return
    }

    if (user && !user?.verifiedUser) {
      showVerifyDialog()
      return
    }

    props.onModalCallback('authModal', {
      showMpModalHeader: true,
      ignoreCookie: false,
      defaultTab: 1,
    })

    return
  }

  return (
    <div
      className={classNames(styles.tier, {
        [styles[props.theme.backgroundVariant]]: true,
      })}
    >
      {props.isMostPopular && (
        <div className={styles.mostPopular}>
          <Text size={TextSizes.bodySml}>Most Popular</Text>
        </div>
      )}
      <div className={styles.tierContainer}>
        <div>
          <div className={styles.tierHead}>
            <Text size={TextSizes.primaryTitleMed} className={styles.tierTitle}>
              <em>My</em>Portfolio{' '}
              {props?.productTitle && (
                <span
                  className={styles.productTitle}
                  dangerouslySetInnerHTML={{ __html: props?.productTitle }}
                />
              )}
            </Text>
            <Text size={TextSizes.bodyMed} className={styles.tierText}>
              {props.productText}
            </Text>
            <Button
              variant={
                props.theme.backgroundVariant === 'blue'
                  ? ButtonVariants.primaryLight
                  : ButtonVariants.primaryDark
              }
              className={styles.tierButton}
              button={{
                type: ButtonTypes.button,
                onClick: onPrimaryButtonClick,
              }}
            >
              {props.buttonText || 'Sign up free'}
            </Button>
          </div>
          <div className={styles.tierBody}>
            {props.introText && (
              <Text
                size={TextSizes.primaryTitleMed}
                className={styles.tierBodyIntroText}
              >
                <span dangerouslySetInnerHTML={{ __html: props.introText }} />
              </Text>
            )}

            {props.featureList && (
              <ul className={styles.tierFeatureList}>
                {props.featureList.map((feature, i) => (
                  <ListItemWithTick
                    text={feature}
                    key={`${props._ref}-feature-${i}`}
                    theme={
                      props.theme.backgroundVariant === 'blue'
                        ? 'dark'
                        : undefined
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          {props?.infoModalText && (
            <button
              className={styles.infoModalText}
              onClick={() => {
                if (props.onModalCallback && props.infoModalReference) {
                  props.onModalCallback(props.infoModalReference)
                }
              }}
            >
              {props?.showInfoModalIcon && <Icon type="SmallInfoIcon" />}
              {props.infoModalText}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

interface TierListProps {
  title?: string
  introText?: string
  tiers: TierBlockProps[]
  footerText?: any
  borderBottom?: boolean
  borderTop?: boolean
}

const TierList = ({
  title,
  introText,
  footerText,
  tiers,
  borderBottom = false,
  borderTop = false,
}: TierListProps) => {
  const { stateDispatch } = useContext(GlobalStoreContext)
  const { getModalDialogFlag } = useFeatureFlag()

  const openModal = (ref: string, props?: any) => {
    let modalType = modalTypes.tierDataUsageDialog

    let theme = undefined

    if (ref === 'speakToOurTeamDialog') {
      modalType = modalTypes.speakToOurTeamDialog
      theme = ReportTheme.secondary
    }

    if (ref === 'authModal') {
      const returningUserData = getLocalStorage(AUTH_CONFIG.RETURNING_USER)
      const isReturningUser = returningUserData ? true : false

      // modalType = modalTypes.authAddToPortfolio
      modalType = !isReturningUser
        ? modalTypes.authSignupDialog
        : modalTypes.authModal

      theme = ReportTheme.primary
    }

    stateDispatch({
      type: GlobalStoreProps.UPDATE_MODAL_STATE,
      payload: {
        isOpen: true,
        component: modalType,
        content: null,
        isDrawer: true,
        props: {
          isReducedWidth: true,
          hideTabs: true,
          showMpModalHeader: true,
          returnToCurrent: true,
          hideModalTitle: true,
          drawerTheme: theme,
          theme: theme,
          ...props,
        },
      },
    })
  }

  return (
    <div
      className={classNames(styles.base, {
        [styles.hasBorder]: borderBottom,
      })}
    >
      <Container>
        <div
          className={classNames({
            [styles.borderBottom]: borderBottom,
            [styles.borderTop]: borderTop,
          })}
        >
          {/* Main Intro */}
          {title || introText ? (
            <div className={styles.baseIntroContainer}>
              {title && (
                <Text
                  size={TextSizes.primaryTitleLrg}
                  className={styles.mainTitle}
                >
                  {title}
                </Text>
              )}
              {introText && (
                <Text size={TextSizes.bodyMed} className={styles.introText}>
                  {introText}
                </Text>
              )}
            </div>
          ) : null}

          {/* Tier Container */}
          <div className={styles.tierListContainer}>
            {tiers.map((tier: TierBlockProps, tierIdx) => (
              <TierBlock
                {...tier}
                key={`sign-up-tier-item-${tier._ref || tierIdx}`}
                onModalCallback={openModal}
              />
            ))}
          </div>

          {/* Floating info footer */}
          {footerText && (
            <div className={styles.footerContainer}>
              <BlockContent
                value={footerText}
                size={TextSizes.bodyLrg}
              ></BlockContent>
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}

export default TierList
